// Entra group catalog + permission toggle matrix.
// Lives at the top of /users (admin only).

import { useEffect, useState } from "react";

interface GroupRow {
  group_key: string;
  entra_group_id: string | null;
  display_name: string;
  default_role: "viewer" | "board_member" | "admin";
  description: string | null;
  approx_member_count: number;
}

interface MatrixCell {
  allowed: boolean;
  explicit: boolean;
  updated_at: string | null;
  updated_by_email: string | null;
}

interface MatrixResponse {
  permission_keys: string[];
  group_keys: string[];
  matrix: Record<string, Record<string, MatrixCell>>;
}

const PERMISSION_LABELS: Record<string, string> = {
  view_bod_brief: "View BOD Brief",
  view_strategic_reviews: "View Strategic Reviews",
  view_external_signals: "View External Signals",
  view_interventions: "View Interventions (PII)",
  view_applicants: "View Applicant Tracker (PII)",
  view_members: "View Members / Roster (PII)",
  view_admin_console: "View Admin Console",
  view_finance: "View Finance (sensitive)",
  edit_finance: "Edit Finance (mutations)",
  approve_finance: "Approve Finance (board sign-off)",
};

// Read-only context: what each permission key grants. Surfaced under the matrix
// so an admin understands the effect of toggling a cell.
const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  view_bod_brief: "Board of Directors brief and governance summaries.",
  view_strategic_reviews: "Strategic review write-ups and findings.",
  view_external_signals:
    "Macro / external signal dashboards (unemployment, incident volume).",
  view_interventions: "Member intervention records — contains PII.",
  view_applicants: "Applicant tracker and recruitment pipeline — contains PII.",
  view_members:
    "Member roster / engagement endpoints — DOB, address, contact, Gov ID, exit interviews. PII.",
  view_admin_console:
    "Admin tooling: AI platform, sync, cross-org, user management.",
  view_finance:
    "Finance section (/finance) and finance APIs — budget, levy, vendors, reconciliation. Read-only.",
  edit_finance:
    "Create/edit/delete finance records (budget lines, committee budgets, projects, authority, reconciliation fields). Paid staff included.",
  approve_finance:
    "Approval / sign-off transitions only: board-approve a project/budget, mark a budget approved, independent reconciliation sign-off. Board members + (NOT paid staff) — FA.PL.001 segregation of duties.",
};

const ROLE_BADGE_CLASS: Record<GroupRow["default_role"], string> = {
  admin:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  board_member:
    "bg-amber-500/15  text-amber-700  dark:text-amber-400  border-amber-500/30",
  viewer:
    "bg-blue-500/15   text-blue-700   dark:text-blue-400   border-blue-500/30",
};

const ROLE_LABEL: Record<GroupRow["default_role"], string> = {
  admin: "Admin",
  board_member: "Board / Senior Staff",
  viewer: "Viewer",
};

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin", ...init });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json()) as T;
}

export default function GroupsAndPermissions() {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [matrix, setMatrix] = useState<MatrixResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pendingCell, setPendingCell] = useState<string | null>(null);

  async function reload() {
    try {
      const [g, m] = await Promise.all([
        fetchJson<{ groups: GroupRow[] }>("/api/admin/rbac/groups"),
        fetchJson<MatrixResponse>("/api/admin/rbac/matrix"),
      ]);
      setGroups(g.groups ?? []);
      setMatrix(m);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function toggle(
    groupKey: string,
    permKey: string,
    currentlyAllowed: boolean,
  ) {
    const cellId = `${groupKey}|${permKey}`;
    setPendingCell(cellId);
    try {
      await fetch("/api/admin/rbac/matrix", {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_key: groupKey,
          permission_key: permKey,
          allowed: !currentlyAllowed,
        }),
      });
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingCell(null);
    }
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-theme-primary">
          Groups & Permissions
        </h2>
        <p className="text-sm text-theme-muted mt-0.5">
          Roles flow from Microsoft Entra group membership. Toggle a cell to
          grant or revoke a permission for the whole group.
        </p>
      </div>

      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Group cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {groups.map((g) => (
          <div
            key={g.group_key}
            className="bg-theme-raised border border-theme-subtle rounded-xl p-4"
          >
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <p className="font-semibold text-theme-primary text-sm">
                {g.display_name}
              </p>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ROLE_BADGE_CLASS[g.default_role]}`}
              >
                {ROLE_LABEL[g.default_role]}
              </span>
            </div>
            <p className="text-xs text-theme-muted mb-2">
              {g.description ?? "—"}
            </p>
            <div className="flex items-center gap-2 text-xs text-theme-muted font-mono">
              <span>Entra ID:</span>
              <span title={g.entra_group_id ?? ""} className="truncate">
                {g.entra_group_id
                  ? `${g.entra_group_id.slice(0, 8)}…`
                  : "— (claim-derived)"}
              </span>
            </div>
            <p className="text-xs text-theme-secondary mt-2">
              ≈ {g.approx_member_count} active users
            </p>
          </div>
        ))}
      </div>

      {/* Permission matrix */}
      {matrix && (
        <div className="bg-theme-raised border border-theme-subtle rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-theme-muted uppercase tracking-wider border-b border-theme-subtle">
                  <th className="text-left py-2.5 px-4 font-semibold">
                    Permission
                  </th>
                  {matrix.group_keys.map((gk) => {
                    const g = groups.find((x) => x.group_key === gk);
                    return (
                      <th
                        key={gk}
                        className="text-center py-2.5 px-4 font-semibold whitespace-nowrap"
                      >
                        {g?.display_name ?? gk}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {matrix.permission_keys.map((pk) => (
                  <tr
                    key={pk}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="py-2.5 px-4 text-theme-primary">
                      <div className="font-medium">
                        {PERMISSION_LABELS[pk] ?? pk}
                      </div>
                      {PERMISSION_DESCRIPTIONS[pk] && (
                        <div className="text-xs text-theme-muted mt-0.5 max-w-md">
                          {PERMISSION_DESCRIPTIONS[pk]}
                        </div>
                      )}
                    </td>
                    {matrix.group_keys.map((gk) => {
                      const cell = matrix.matrix[gk]?.[pk];
                      const cellId = `${gk}|${pk}`;
                      const pending = pendingCell === cellId;
                      const allowed = cell?.allowed ?? false;
                      return (
                        <td key={gk} className="text-center py-2.5 px-4">
                          <button
                            type="button"
                            onClick={() => toggle(gk, pk, allowed)}
                            disabled={pending}
                            className={`inline-flex items-center justify-center w-12 h-6 rounded-full border transition-colors disabled:opacity-50 ${
                              allowed
                                ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300"
                                : "bg-zinc-500/10 border-zinc-500/30 text-zinc-400 hover:bg-zinc-500/20"
                            }`}
                            title={
                              cell?.updated_by_email
                                ? `Last toggled by ${cell.updated_by_email} at ${cell.updated_at ?? ""}`
                                : "Click to toggle"
                            }
                          >
                            <span className="text-[11px] font-semibold uppercase tracking-wider">
                              {allowed ? "On" : "Off"}
                            </span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-theme-muted px-4 py-2.5 border-t border-theme-subtle italic">
            Toggling a cell writes to <code>dash_rbac_permissions</code> and
            takes effect immediately for any session whose user is in that
            group. Server-side route gates that haven't been migrated to the
            permission system yet still enforce the role baseline.
          </p>
        </div>
      )}
    </section>
  );
}
